import Typography from '@material-ui/core/Typography';
import React from 'react';
import { Observe } from 'rewire-core';
import { ICell } from 'rewire-grid';
import { withStyles } from 'rewire-ui';
import IconButton from '@material-ui/core/IconButton';
import RefreshIcon from '@material-ui/icons/Refresh';
import RowSelectionGrid from '../ui/RowSelectionGrid';
import ViewTabs from '../ui/ViewTabs';
import { roundTo } from '../utility/DataConverters';
import { makeStyles, StyleType, Theme } from '../utility/styles';
import AccrualDialog from './AccrualDialog';
import accrualDialogModel from './AccrualDialogModel';
import EmployeeTimeOffTypeView from './EmployeeTimeOffTypeView';
import employeeTimeOffTypeViewTabs from './EmployeeTimeOffTypeViewTabs';
import timeOffBalancesDialogModel from '../schedule/TimeOffBalancesDialogModel';

const styles = (theme: Theme) => ({
  header: {
    display: 'flex',
    flex: '1 0 auto',
    alignItems: 'center',
    marginTop: '10px',
  },
  dateSelection: {
    flex: '0 0 50%',
  },
  refresh: {
    // flex: '0 0 auto',
    color: theme.palette.success.main,
    width: '40px',
    height: '40px',
    marginLeft: 'auto',
  },
  button: {
    marginLeft: '5px',
    textTransform: 'none',
  },
  icon: {
    marginRight: '6px',
    marginLeft: '-6px',
    color: theme.palette.success.main,
  },
  divider: {
    marginTop: '12px',
  },
  grid: {
    marginTop: '6px',
    '& th': {
      whiteSpace: 'pre-wrap',
      paddingTop: '3px',
      paddingBottom: '3px',
    },
    '& td.requestedOnHoldCell': {
      borderLeft: 'solid 2px black',
    },
  },
  timeOffBalancesTitle: {
    marginBottom: '10px',
  },
  employeeTabsContainer: {
    marginBottom: '10px',
  },
});

const useStyles = makeStyles(styles);

export default React.memo(() => {
  const classes = useStyles();
  const gridFontSizes = {
    header: '0.75em',
    body: '0.75em',
    groupRow: '1em',
    toggleMenu: '0.8em',
  };

  React.useEffect(() => {
    timeOffBalancesDialogModel.activate();
    // returned function will be called on component unmount
    return () => {
      timeOffBalancesDialogModel.deactivate();
    };
  }, []);

  return (
    <Observe
      render={() => (
        <>
          <Typography className={classes.timeOffBalancesTitle}>Time Off Balances</Typography>
          <div className={classes.header}>
            <timeOffBalancesDialogModel.asofField.Editor className={classes.dateSelection} />
            <IconButton
              title="Push to refresh balances to their latest value"
              className={classes.refresh}
              onClick={() => timeOffBalancesDialogModel.refresh()}
              aria-label="Refresh"
            >
              <RefreshIcon />
            </IconButton>
          </div>

          <RowSelectionGrid
            grid={timeOffBalancesDialogModel.timeOffTypesGrid}
            className={classes.grid}
            gridFontSizes={gridFontSizes}
          />

          <span className={classes.header}>
            <div className={classes.employeeTabsContainer}>
              <ViewTabs tabs={employeeTimeOffTypeViewTabs} />
            </div>
          </span>
          <EmployeeTimeOffTypeView />

          <AccrualDialog model={accrualDialogModel} />
        </>
      )}
    />
  );
});

const timeOffTypeBalanceCellStyles = (_theme: Theme) => ({
  root: {
    marginRight: '10px',
    lineHeight: '1.6',
  },
});

export const TimeOffTypeBalanceCell: React.FunctionComponent<{
  cell: ICell;
  classes?: StyleType<typeof timeOffTypeBalanceCellStyles>;
}> = React.memo(
  withStyles(timeOffTypeBalanceCellStyles, (props) => {
    const { cell, classes } = props;
    const showUnits = cell.row.data.trackingUnits;
    const showAmount = cell.row.data.trackingAmounts;
    const hours = roundTo(cell.value.hours, 6);
    const amount = roundTo(cell.value.amount, 3);

    return (
      <Observe
        render={() => (
          <div className={classes!.root}>
            {showUnits && <div style={{ textAlign: 'right' }}>{hours}</div>}
            {showAmount && <div style={{ textAlign: 'right' }}>${amount.toFixed(2)}</div>}
          </div>
        )}
      />
    );
  }),
  () => false,
);
